// English source strings of the structured block reasons (decision D37 "A block always says what to do"): the short
// title and the plain-language explanation of each code, the decisions and their confirmations, the evidence and the
// "what blocks what" lines. Merged into `en`; pt-BR lives in pt-BR-blocks.ts (complete, checked by the type).
export const enBlocks = {
  "block.title.environment": "The test environment cannot run the tests",
  "block.title.environment.command_not_found": "The test environment is missing a program",
  "block.title.environment.not_executable": "A test program cannot be executed",
  "block.title.environment.module_missing": "The project's dependencies are not installed",
  "block.title.environment.sandbox_unavailable": "The test sandbox was unavailable",
  "block.title.environment.network_unavailable": "The tests needed the internet",
  "block.title.environment.runner_timeout": "The tests took longer than allowed",
  "block.title.quality_after_premium": "The premium model could not finish it either",
  "block.title.quality_stalled": "The agents stopped without finishing",
  "block.title.council_denied": "The council kept the task blocked",
  "block.title.budget_limit": "Spending limit reached",
  "block.title.unknown_cost": "Some costs are still unknown",
  "block.title.route_unavailable": "No AI model is available for this task",
  "block.title.configuration": "A configuration is missing",
  "block.title.context_capacity": "The task is too large for the available models",
  "block.title.model_divergence": "The provider answered with another model",
  "block.title.run_ceiling": "This run reached its attempt limit",
  "block.title.project_not_ready": "The project cannot run commands yet",
  "block.title.dependency_blocked": "Waiting for another task",
  "block.title.approval_needed": "Waiting for your decision",
  "block.title.integration_conflict": "Conflict with the main branch",
  "block.title.delivery_failed": "The delivery failed",
  "block.title.fix_task_opened": "A fix task took over the work",
  "block.title.requirement_ambiguous": "The requirement is ambiguous",
  "block.title.manual": "Stopped by a person",
  "block.title.unknown": "The task is stopped",

  "block.explain.environment.command_not_found":
    "The {command} could not even start: the program \"{binary}\" does not exist in the isolated environment where the tests run{exit}. This is not the model's fault — no paid model was used and no retry was spent. Fix the test command or have the environment prepared, then try again.",
  "block.explain.environment.not_executable":
    "The program \"{binary}\" exists but cannot be executed (permission denied{exit}). This is not the model's fault. Fix the test command or the file permissions, then try again.",
  "block.explain.environment.module_missing":
    "The {command} started, but the project's dependencies are not installed in the test environment (\"{binary}\" was not found). This is not the model's fault. Install the dependencies in the environment (prepare command) or adjust the test command, then try again.",
  "block.explain.environment.sandbox_unavailable":
    "The isolated test environment (sandbox) was offline when the tests were about to run. Nothing was judged and no retry was spent. Try again once it is back.",
  "block.explain.environment.network_unavailable":
    "The {command} tried to reach the internet, but tests run without network. This is not the model's fault. Make the tests work offline (install the dependencies beforehand), then try again.",
  "block.explain.environment.runner_timeout":
    "The {command} ran longer than the runner allows and was stopped. This is not counted against the model. Make the tests faster or raise the time limit, then try again.",
  "block.explain.quality_after_premium":
    "The economy models and then the premium model tried, but the work did not pass the tests or the review ({cause}). The system goes up to the premium model only once per task, so it stopped here for you to decide: retry with the same or another model, adjust the task, or split it into smaller ones.",
  "block.explain.quality_stalled":
    "The attempts did not pass the tests or the review and there was not enough progress to keep going alone ({cause}). Adjust the task, try another model or split the work.",
  "block.explain.council_denied": "The review council looked at the block ({previous}) and decided not to retry on its own: {reasons}",
  "block.explain.budget_limit": "The project's spending limit{limit} stops new paid calls. Raise the limit in the settings or approve this cost for one attempt.",
  "block.explain.unknown_cost": "The cost of {calls} paid call(s) could not be confirmed, so new paid calls stay blocked under the spending limit until it is reconciled. An administrator must check each cost against the provider's records and enter the real charge, or 0 if the provider did not charge it.",
  "block.explain.route_unavailable": "The AI provider's connection or key is not working, or the model was not cleared for real use. Check the connections in the settings and try again.",
  "block.explain.configuration": "A required configuration is missing (for example a reviewer model). Fix it in the project settings and try again.",
  "block.explain.context_capacity": "No configured model can receive all the mandatory context of this task. Split the task or configure a model with a larger window.",
  "block.explain.model_divergence": "The AI provider used a different model than the one configured, so the task stopped for safety. Review the model configuration and try again.",
  "block.explain.run_ceiling": "This run used {used} of {limit} {kind}. Try again to open a new run, or adjust or split the task.",
  "block.explain.run_ceiling.attempts": "attempts",
  "block.explain.run_ceiling.model_calls": "model calls",
  "block.explain.run_ceiling.other": "of its budget",
  "block.explain.project_not_ready": "The tests cannot run because the project is not cleared to execute commands (sandbox or execution permission). Check the readiness in the settings.",
  "block.explain.dependency_blocked": "This task depends on {list}, which is stopped. It starts by itself once that task is resolved.",
  "block.explain.approval_needed": "The council asked for a human decision ({code}). Answer the question on this task.",
  "block.explain.integration_conflict": "The work conflicts with {base}{files}. Trying again redoes the task on top of the current version.",
  "block.explain.delivery_failed": "The pull request could not be opened or the branch could not be pushed. Check the GitHub connection and try again.",
  "block.explain.fix_task_opened": "Task {fix} was opened to fix the integrated result; follow the work there.",
  "block.explain.requirement_ambiguous": "The PRD requirement behind this task is ambiguous: {detail}. Edit the task to say clearly what must be done.",
  "block.explain.manual": "Someone blocked this task by hand.",
  "block.explain.unknown": "The task stopped for a reason that cannot be explained in detail yet. See the technical details or ask the assistant.",

  "block.command.test": "test command",
  "block.command.build": "build command",
  "block.command.prepare": "prepare command",
  "block.command.integration": "integration tests",
  "block.exit": " (exit {code})",
  "block.exitInline": ", exit {code}",
  "block.unknownProgram": "a program",
  "block.unknownCause": "no further detail",
  "block.limitAmount": " ({amount})",
  "block.inFiles": " in {files}",
  "block.baseBranch": "the main branch",
  "block.reasonsNone": "no reason was given.",

  "block.cause.quality_gate_failed": "the tests failed",
  "block.cause.critic_rejected": "the reviewer rejected it",
  "block.cause.repeated_test_failure": "the integration tests kept failing",
  "block.cause.no_progress": "no progress between attempts",
  "block.cause.context_capacity": "the context did not fit",
  "block.cause.capability_missing": "the model lacks a needed capability",
  "block.cause.provider_unavailable": "the provider was unavailable",
  "block.cause.quota_exhausted": "the quota ran out",
  "block.cause.high_risk_policy": "risk policy",
  "block.cause.human_override": "a person's decision",

  "block.councilReason.retriedOnce": "it already approved one retry for this cause and the task was blocked again.",
  "block.councilReason.defect": "a repeated failure points to a problem in the task itself; rework it instead of retrying.",
  "block.councilReason.loop": "a second retry would loop.",
  "block.councilReason.fixTask": "a linked fix task already carries the work.",
  "block.councilReason.outsideAgents": "the action that unblocks it is outside the agents' reach: a person must change the configuration.",

  "block.decision.retry_now": "Retry now",
  "block.decision.accept_without_tests": "Accept without tests",
  "block.decision.retry_same_model": "Retry with the same model",
  "block.decision.retry_other_model": "Retry with another model",
  "block.decision.cancel_task": "Cancel task",
  "block.decision.override_retry": "Override and retry",
  "block.decision.release_dependents": "Release dependents",
  "block.decision.approve_cost": "Approve this cost",
  "block.decision.reconcile_cost": "Reconcile cost",
  "block.decision.edit_test_command": "Edit the test command",
  "block.decision.ask_assistant": "Ask the assistant",
  "block.decision.ask_assistant.environment": "Ask the assistant to fix the environment",
  "block.decision.edit_task": "Edit the task",
  "block.decision.split_task": "Split into smaller tasks",
  "block.decision.open_blocking_task": "Open the blocking task",
  "block.decision.raise_limit": "Raise the limit",
  "block.decision.open_settings": "Open settings",

  "block.confirm.title": "Confirm: {decision}",
  "block.confirm.accept_without_tests": "Task #{number} will move on WITHOUT running its tests. The waiver is recorded with your name. Use it only when you are sure the work does not need the tests.",
  "block.confirm.retry_same_model": "Task #{number} gets one more attempt on the same model, up to {cost}.",
  "block.confirm.retry_other_model": "Task #{number} gets one more attempt on the model you choose, up to {cost}.",
  "block.confirm.cancel_task": "Task #{number} will be cancelled. Tasks that depend on it keep waiting until you release them.",
  "block.confirm.override_retry": "The council decided not to retry task #{number}. You are overriding that decision; it is recorded with your name.",
  "block.confirm.release_dependents": "{n} task(s) stop waiting for #{number} and can start without it. Their other dependencies stay.",
  "block.confirm.approve_cost": "Task #{number} gets one paid attempt up to the cost you approve.",
  "block.confirm.reconcile_cost": "Check each amount against the provider's records and enter the real charge, in whole cents. Enter 0 if the provider did not charge the call. Nothing is estimated: the note names the evidence you checked.",
  "block.reconcile.calls": "Paid calls with unknown cost",
  "block.reconcile.callState": "State: {state}",
  "block.reconcile.callError": "error: {error}",
  "block.reconcile.costLabel": "Real charge: {call}, in cents",
  "block.reconcile.centsHelp": "Whole cents (0 if not charged): {usd}",
  "block.reconcile.invalidCents": "Enter whole cents from 0 to 1000000, with no decimals.",
  "block.reconcile.note": "Evidence checked",
  "block.reconcile.noteHelp": "Name the provider records you checked, such as the statement, the invoice or the dashboard.",
  "block.reconcile.noteRequired": "Say which provider records you checked.",
  "block.reconcile.fixFields": "Fix the highlighted fields before confirming.",
  "block.reconcile.total": "Total to record: {cost} across {n} call(s).",
  "block.reconcile.none": "No paid call with unknown cost is listed right now. Refresh the task before deciding.",
  "block.reconcile.batch": "This decision takes up to {max} calls at a time. Showing the first {max} of {n}; reconcile the rest afterwards.",
  "block.confirm.model": "Model",
  "block.confirm.cost": "Cost ceiling (US$)",
  "block.confirm.costHint": "The attempt stops at this amount.",
  "block.confirm.go": "Confirm",

  "block.whatToDo": "What you can do",
  "block.askAssistantShort": "Ask the assistant",
  "block.technicalDetails": "Technical details",
  "block.evidence": "Evidence",
  "block.evidenceTail": "Last lines of the test log",
  "block.action.title": "Who acts next",
  "block.action.next": "Next step",
  "block.action.resume": "Resumes when",
  "block.action.owner.system": "The system repairs this on its own",
  "block.action.owner.agent": "An agent handles this",
  "block.action.owner.human": "A person must decide",
  "block.action.owner.integration": "An external integration must respond",
  "block.dependents": "{n} task(s) wait for this one",
  "block.dependentsList": "Waiting: {list}",
  "block.blockedBy": "Blocked by {list}",
  "block.needsRole": "Needs the {role} role",
  "block.needsRoleFor": "{decision} needs the {role} role.",
  "block.decided": "Decision recorded for task #{number}: {decision}.",
  "block.decidedRun": "Decision recorded for task #{number}: {decision}. The agents started again.",
  "block.decidedReleased": "{n} task(s) released from #{number}.",
  "block.decisionFailed": "The decision was not applied: {reason}",
  "block.seeWhatToDo": "See what to do",
  "block.loading": "Loading what blocks this task…",
  "block.attentionTitle": "Task #{number} needs you: {title}",
  "block.role.observer": "observer",
  "block.role.reviewer": "reviewer",
  "block.role.operator": "operator",
  "block.role.maintainer": "maintainer",
  "block.role.admin": "admin",

  "block.prompt.environment": "Task #{number} \"{title}\" is blocked because its test command could not run in the sandbox (program {binary}{exit}). How do I fix the environment so the tests run?",
  "block.prompt.default": "Why is task #{number} \"{title}\" blocked, and what do you recommend I do?",
  "block.prompt.split": "Split task #{number} \"{title}\" into smaller, independent tasks with clear acceptance criteria.",

  "editTask.title": "Edit task #{number}",
  "editTask.name": "Title",
  "editTask.description": "Description",
  "editTask.saved": "Task #{number} saved.",

  "testCommand.title": "Test command",
  "testCommand.hint": "Runs in the sandbox without network for every task. The program must exist there (readiness checks it).",
  "testCommand.workspace": "Workspace {path}",
  "testCommand.saved": "Test command saved. Validate the project again to check it.",
  "testCommand.none": "No workspace is registered for this project yet.",

  "readiness.cause.testProgramMissing": "The test command \"{command}\" runs \"{program}\", which does not exist in the test sandbox. Install it in the sandbox image or change the test command.",
} as const;
